import {
  ConfirmContactValidationCodeRequestType,
  ContactValidationCodeRequestType,
  CreateProductRequestType,
  ListPublishableProductsType,
  SoldProductsByCompanySelectOptionProps,
  useFetchProductsProps,
} from "@@types/IProduct";
import { ProductSettingsSchemaType } from "@components/pages/product/Settings/validator";
import { CompanyContext } from "@contexts/company/CompanyContext";
import { api } from "@services/apiClient";
import {
  fetchProduct,
  fetchProductEvaluationReason,
  fetchProducts,
  fetchProductsPublishable,
  fetchProductsSold,
} from "@services/http/products";
import { fetchProductsSoldByCompany } from "@services/http/products/Product.resources";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format, limitStringSize } from "@utils/utils";
import { SelectOption } from "@_types/shared";
import { useContext } from "react";

export function useFetchProducts() {
  const { currentCompany } = useContext(CompanyContext);

  return useQuery(["/products", currentCompany?.uuid], () => fetchProducts({ page: 1, pageSize: 999 }), {
    enabled: !!currentCompany?.uuid,
  });
}

export function useFetchProductsPerPage({ params }: useFetchProductsProps) {
  const { currentCompany } = useContext(CompanyContext);
  const paramsArray = [
    params?.pageSize,
    params?.page,
    params?.format,
    params?.productionRole,
    params?.search,
    params?.status,
  ];
  const query = useQuery(
    ["/products", currentCompany?.uuid, paramsArray],
    () => fetchProducts(params, { search: params?.search }),
    {
      enabled: !!currentCompany?.uuid,
    }
  );
  return {
    ...query,
    isLoading: query.isLoading && query.fetchStatus !== "idle",
  };
}

export function useFetchProductsByFilter({ params }: useFetchProductsProps) {
  const { currentCompany } = useContext(CompanyContext);
  const query = useQuery(
    ["/products", currentCompany?.uuid, params?.pageSize, params?.page],
    () => fetchProducts(params),
    {
      enabled: !!currentCompany?.uuid,
    }
  );
  return {
    ...query,
    isLoading: query.isLoading && query.fetchStatus !== "idle",
  };
}

export function useFetchProductsPublishable() {
  const { currentCompany } = useContext(CompanyContext);

  return useQuery(
    ["/products", "/publishable", currentCompany?.uuid],
    () =>
      fetchProductsPublishable({
        page: 1,
        pageSize: 100,
      }),
    {
      enabled: !!currentCompany?.uuid,
      staleTime: 1000 * 10, // 10 seconds
    }
  );
}

export function useFetchProductsSold() {
  return useQuery(["/products", "/sold"], () => fetchProductsSold(), {
    enabled: true,
    staleTime: 0, // 30 seconds
  });
}

export function useProductsSoldByCompanyAsSelectOptions() {
  const { data, fetchStatus, isFetched, isLoading } = useFetchProductsSoldByCompany();

  let options: SelectOption[] = [];

  if (data && isFetched && !isLoading) {
    if (Array.isArray(data.data.data)) {
      options = data.data.data.map((product: SoldProductsByCompanySelectOptionProps) => ({
        value: product.uuid,
        label: product.name,
      }));
    } else {
      console.error("Erro: data.data.data não é um array ou está indefinido", data);
    }
  }

  return { options, isDisabled: !isFetched || !options.length, isLoading: isLoading && fetchStatus !== "idle" };
}

export function useFetchProductsSoldByCompany() {
  const { currentCompany } = useContext(CompanyContext);

  return useQuery(["/products", "/sold", "/company", currentCompany?.uuid], () => fetchProductsSoldByCompany(), {
    enabled: !!currentCompany?.uuid,
    staleTime: 0,
  });
}

export function useFetchProductById(uuid: string) {
  const query = useQuery(["/products", uuid], () => fetchProduct(uuid), {
    enabled: !!uuid && uuid.length > 0 && uuid !== "all",
    staleTime: 0,
    refetchOnMount: false,
  });

  return {
    ...query,
    product: query.data,
  };
}

export function useFetchProductSales(uuid: string) {
  return useQuery(["/products-sales", "/sales", uuid], () => fetchProduct(uuid), {
    enabled: uuid.length > 0,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 30, // 30 seconds
  });
}

export function useFetchProductEvaluationReasonById(uuid: string) {
  return useQuery(["/products", uuid, "/evaluation-reason"], () => fetchProductEvaluationReason(uuid), {
    enabled: uuid.length > 0,
  });
}

export function useCreateProduct() {
  return useMutation((data: CreateProductRequestType) => {
    return api.post("/products", data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  });
}

export function useDeleteProduct() {
  return useMutation((productUuid: string) => {
    return api.delete(`/products/${productUuid}`);
  });
}

export function useUpdateProduct() {
  return useMutation(({ uuid, data }: { uuid: string; data: ProductSettingsSchemaType }) => {
    return api.put(`/products/${uuid}`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  });
}

export function useProductsAsSelectoptions() {
  const { data, isLoading, isFetched } = useFetchProducts();

  // FIXME: Ajustar para o backend entregar o label de todos
  let productOptions: { value: string; label: string }[] = [];
  if (isFetched && data) {
    Object.keys(data?.data).forEach(function (key: any) {
      productOptions.push({
        value: data.data[key].uuid,
        label: data.data[key].name,
      });
    });
  }

  return { productOptions, isLoading, isFetched };
}

export function useProductsSoldAsSelectOptions() {
  const { data, error, status, isLoading } = useFetchProductsSold();
  let productOptions: any[] = [];

  if (data) {
    Object.keys(data.data).forEach(function (key: any) {
      productOptions.push({
        value: data["data"][key]["uuid"],
        label: data["data"][key]["name"],
      });
    });
  }
  return { productOptions, error, status, isLoading };
}

export function useValidateContact() {
  return useMutation(async (data: ContactValidationCodeRequestType) => {
    return await api.post(
      `/products/${data.productUuid}/contact-validation`,
      {
        contactType: data.contactType,
        contact: data.contact,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  });
}

export function useValidateCode() {
  return useMutation(async (data: ConfirmContactValidationCodeRequestType) => {
    return await api.put(
      `/products/${data.productUuid}/contact-validation/${data.uuid}`,
      {
        uuid: data.productUuid,
        code: data.code,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  });
}

export function useFetchPublishableProductsAsSelect() {
  const { data, isLoading, isFetched, isFetching } = useFetchProductsPublishable();

  let productOptions: any[] = [];

  if (isFetched && data) {
    data.data.map((product: ListPublishableProductsType) => {
      productOptions.push({
        value: product.uuid,
        label: limitStringSize(30, product.name) || "Sem nome",
        offers: product.offers.map(offer => {
          return {
            value: offer.uuid,
            label: limitStringSize(30, offer.name) + " - " + format.money(offer.price),
            type: offer.billingType,
            price: offer.price,
          };
        }),
      });
    });
  }

  return { productOptions, isLoading, isFetched, isFetching };
}
